import { useEffect, useState, type ReactNode } from "react";
import { baglantiSemasi, yerelGunBasi, yerelHaftaBasi } from "@koclukpanel/contracts";
import {
  Buton,
  DURUM_SINIFLARI,
  DurumRozeti,
  HAFTA_GUNLERI,
  Ikon,
  Iskelet,
  Rozet,
  cn,
  dersRengi,
  type UnkDurum,
} from "@koclukpanel/ui";
/*
  DURUM DİLİ KOÇUN "ÖĞRENCİ GÖZÜ" İLE TEK KAYNAK. Koç (sürüm 9a) öğrencinin programını bu yardımcılarla
  çiziyor; öğrenci aynı haftayı aynı sözcüklerle ve aynı renkle görmeli — iki kopya olsaydı biri
  düzeltilince koç ile öğrenci aynı görevi iki farklı durumda görürdü. Dosya SAF (yalnız tip içe
  aktarıyor), arayüz bağımlılığı yok.
*/
import {
  OGRENCI_SUZGECLERI,
  dersKisaltmasi,
  gorunenDurum,
  noktaliTarih,
  suzgeceUyar,
  type OgrenciSuzgeci,
} from "../../panel/src/program-durum";
import { ApiHatasi, haftalikGorevler, type Gorev } from "./api";
import { haftaGunleri } from "./HaftalikProgramGorunumu";

/**
 * GEÇMİŞ GÜN GİRİŞİ — F-STV-04a (A10, 05.10.2026).
 *
 * Veri yalnız "Bugün"e girilebiliyordu; dünü unutan öğrenci telafi edemiyordu.
 * İçinde bulunulan haftanın GEÇMİŞ günlerinde hedefe ulaşmamış (sarı/kırmızı)
 * görevler sonuç girişine açık. Kayıt akışı Bugün ile AYNI (`VeriGirisi`/
 * `Yapamadim`, çevrimdışı kuyruk); sayaç yok — sunucu geçmiş günde sayacı
 * reddeder, süre formda elle girilir. Kapalı hafta ve gelecek gün burada hiç
 * açılmıyor; asıl kapı sunucuda (`gorev-tarih.ts`).
 */
export interface GecmisGunGirisi {
  onVeriGir: (g: Gorev) => void;
  onYapamadim: (g: Gorev) => void;
  bekleyen: ReadonlySet<string>;
  /** Kayıt gönderilince artar; liste sunucudan yeniden okunur. */
  yenile: number;
}

/** Bu haftanın bugünden ÖNCEKİ günlerinde, hedefe ulaşmamış ve girilebilir görevler. */
export function eksikGecmisGorevler(gorevler: readonly Gorev[], simdi = new Date()): Gorev[] {
  const bugun = yerelGunBasi(simdi).getTime();
  const haftaBasi = yerelHaftaBasi(simdi).getTime();
  return gorevler.filter((g) => {
    const gun = new Date(g.itemDate).getTime();
    return (
      gun >= haftaBasi &&
      gun < bugun &&
      !g.isExcused &&
      g.status !== "POSTPONED" &&
      g.renk !== "YESIL"
    );
  });
}

/**
 * HAFTALIK PROGRAM — sürüm 9b, UNİKOÇ `WeeklySchedule-ViewAsStudent` birebir
 * (müşteri notu: "Öğrenci panelinde program bu şekilde görünür. Yeşil olması
 * tamamlayıp verileri girdiği anlamına gelir.")
 *
 *   başlık kartı  ad + "Haftalık Program" hapı, tarih aralığı, ‹ · takvim · ›, renk göstergesi
 *   süzgeç        Görünüm: Tümü · Bugün · Tamamlanan · Tamamlanmayan · Planlanmış
 *   gün blokları  açılır şerit + kart ızgarası (telefonda tek, tablette iki, masaüstünde üç sütun)
 *   kart          sol renkli çizgi, üstten beyaza eriyen zemin, ders dairesi, durum rozeti,
 *                 Konu/Kaynak/Soru, PDF/video hapları, Notlar, "rapor girmedin" uyarısı
 *
 * BİLİNÇLİ İKİ SAPMA (karar 07.10.2026 ve işlev):
 *  1. Hafta PAZARTESİ başlıyor (ONERI §5.2); eski panel Cumartesi'den başlatıyordu.
 *  2. Kartın altındaki "Salt-okunur mod" şeridi öğrencide yalnız giriş KAPALIYKEN
 *     var. Bugünün ve bu haftanın eksik kalan geçmiş günlerinin kartında yerine
 *     "Sonucu gir" / "Yapamadım" duruyor: öğrencinin günlük doğru/yanlış girişi
 *     mevcut akışla (`VeriGirisi`/`Yapamadim`, çevrimdışı kuyruk) kalıyor.
 *
 * F-STV-02a: renk SUNUCUDAN (`renk`); istemci sonuçtan renk türetmez.
 */
export function HaftalikProgram({
  ogrenciId,
  ad,
  onRevizyon,
  gecmisGunGirisi,
}: {
  ogrenciId: string;
  /** Başlık kartındaki ad; bilinmiyorsa (çevrimdışı) "Haftalık Program" yazıyor. */
  ad?: string | null;
  onRevizyon: () => void;
  gecmisGunGirisi?: GecmisGunGirisi;
}) {
  const buHafta = yerelHaftaBasi().toISOString().slice(0, 10);
  const [hafta, setHafta] = useState(buHafta);
  const gunler = haftaGunleri(hafta);
  const haftaDegistir = (adim: number) => {
    const tarih = new Date(`${hafta}T00:00:00.000Z`);
    tarih.setUTCDate(tarih.getUTCDate() + adim);
    setHafta(tarih.toISOString().slice(0, 10));
  };
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Başlık kartı — beyaz, eski paneldeki ayrı kalıp (tarama-2 §3.3/1). */}
      <div className="bg-notr-0 border-mor-kenar flex flex-col gap-3 rounded-[1.25rem] border p-4 shadow-(--golge-unk-kart) md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span
            aria-hidden="true"
            className="text-notr-0 grid size-12 shrink-0 place-items-center rounded-full bg-(image:--gradyan-unk-birincil) md:size-14"
          >
            <Ikon ad="program" className="size-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-2">
              <span className="text-metin-koyu text-lg font-bold">{ad || "Programım"}</span>
              <span className="text-notr-0 rounded-rozet bg-(image:--gradyan-unk-birincil) px-2 py-0.5 text-[0.6875rem] font-bold whitespace-nowrap">
                Haftalık Program
              </span>
            </h1>
            <p className="text-metin-ikincil mt-0.5 flex items-center gap-1.5 text-sm tabular-nums">
              <Ikon ad="takvim" className="size-4" />
              {noktaliTarih(gunler[0]!)} — {noktaliTarih(gunler[6]!)}
            </p>
          </div>
          <nav
            aria-label="Hafta gezinmesi"
            className="flex shrink-0 items-center gap-2 max-sm:w-full max-sm:justify-end"
          >
            <KareDugme etiket="Önceki hafta" onClick={() => haftaDegistir(-7)}>
              <span aria-hidden="true">‹</span>
            </KareDugme>
            <KareDugme etiket="Bu hafta" onClick={() => setHafta(buHafta)}>
              <Ikon ad="takvim" className="size-4" />
            </KareDugme>
            <KareDugme etiket="Sonraki hafta" onClick={() => haftaDegistir(7)}>
              <span aria-hidden="true">›</span>
            </KareDugme>
          </nav>
        </div>
        <ul aria-label="Renklerin anlamı" className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {(
            [
              ["tamamlandi", "Tamamlandı"],
              ["bekliyor", "Bekliyor (Bugün)"],
              ["planli", "Planlanmış"],
              ["eksik", "Tamamlanmadı"],
            ] as const
          ).map(([durum, etiket]) => (
            <li key={durum} className="text-metin-ikincil flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cn("size-2.5 rounded-full", DURUM_SINIFLARI[durum].nokta)}
              />
              {etiket}
            </li>
          ))}
        </ul>
      </div>

      <HaftaVerisi
        key={`${ogrenciId}-${hafta}`}
        ogrenciId={ogrenciId}
        hafta={hafta}
        gecmisGunGirisi={gecmisGunGirisi}
      />

      <div className="border-mor-kesikli bg-notr-0/70 flex flex-col gap-2 rounded-[1.25rem] border border-dashed p-4 sm:flex-row sm:items-center">
        <p className="text-metin-ikincil flex-1 text-sm">
          Program yayında; doğrudan değiştirilemez. Sana uymayan bir şey varsa koçundan değişiklik
          iste.
        </p>
        <Buton gorunum="ikincil" onClick={onRevizyon}>
          Değişiklik iste
        </Buton>
      </div>
    </div>
  );
}

function HaftaVerisi({
  ogrenciId,
  hafta,
  gecmisGunGirisi,
}: {
  ogrenciId: string;
  hafta: string;
  gecmisGunGirisi?: GecmisGunGirisi;
}) {
  const [gorevler, setGorevler] = useState<Gorev[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [deneme, setDeneme] = useState(0);
  useEffect(() => {
    // Hızlı hafta/öğrenci geçişinde eski isteğin yanıtı yeni haftaya yazılmasın.
    let etkin = true;
    void haftalikGorevler(ogrenciId, `${hafta}T00:00:00.000Z`).then(
      (veri) => {
        if (etkin) setGorevler(veri);
      },
      (err: unknown) => {
        if (etkin) setHata(err instanceof ApiHatasi ? err.message : "Haftalık program alınamadı.");
      },
    );
    return () => {
      etkin = false;
    };
  }, [ogrenciId, hafta, deneme, gecmisGunGirisi?.yenile]);
  if (hata)
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 w-full px-3 py-2 text-sm">
          {hata}
        </p>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            setHata(null);
            setDeneme((d) => d + 1);
          }}
        >
          Tekrar dene
        </Buton>
      </div>
    );
  if (gorevler === null) return <Iskelet satir={4} />;
  return <HaftaGunleri hafta={hafta} gorevler={gorevler} gecmisGunGirisi={gecmisGunGirisi} />;
}

function HaftaGunleri({
  hafta,
  gorevler,
  gecmisGunGirisi,
}: {
  hafta: string;
  gorevler: readonly Gorev[];
  gecmisGunGirisi?: GecmisGunGirisi;
}) {
  const gunler = haftaGunleri(hafta);
  const bugun = yerelGunBasi().toISOString().slice(0, 10);
  const buHaftaMi = gunler.includes(bugun);
  const [suzgec, setSuzgec] = useState<OgrenciSuzgeci>("Tümü");
  /*
    HANGİ GÜNLER AÇIK BAŞLIYOR. Eski panelde dolu günler açık, boşlar yalnız
    şeritti. Telefonda yedi dolu gün tek ekranda 5–6 kaydırma demek; bu hafta
    açıkken BUGÜN ve rapor girilmemiş görevi olan geçmiş günler açık başlıyor
    (öğrencinin açılışta sorduğu iki soru: "bugün ne var", "neyi eksik
    bıraktım"); başka bir haftada görev olan bütün günler.
  */
  const [acik, setAcik] = useState<ReadonlySet<string>>(() =>
    buHaftaMi
      ? new Set([bugun, ...eksikGecmisGorevler(gorevler).map((g) => g.itemDate.slice(0, 10))])
      : new Set(gunler.filter((g) => gorevler.some((x) => x.itemDate.slice(0, 10) === g))),
  );
  const gunDegistir = (gun: string): void =>
    setAcik((once) => {
      const yeni = new Set(once);
      if (yeni.has(gun)) yeni.delete(gun);
      else yeni.add(gun);
      return yeni;
    });
  // F-STV-04a — girişe açık görevler: bugünün mazeretsiz görevleri + bu haftanın eksik geçmişi.
  const girilebilir = new Set(
    gecmisGunGirisi
      ? [
          ...eksikGecmisGorevler(gorevler).map((g) => g.id),
          ...gorevler
            .filter(
              (g) => g.itemDate.slice(0, 10) === bugun && !g.isExcused && g.status !== "POSTPONED",
            )
            .map((g) => g.id),
        ]
      : [],
  );
  const eksikSayisi = gecmisGunGirisi && buHaftaMi ? eksikGecmisGorevler(gorevler).length : 0;

  return (
    <>
      {/* Süzgeç çubuğu — "Görünüm:" + haplar (aktif: mor ince çerçeve). */}
      <div className="bg-notr-0 border-mor-kenar flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[1.25rem] border px-4 py-3">
        <span className="text-metin-ikincil flex items-center gap-1.5 text-sm">
          <Ikon ad="filtre" className="size-4" />
          Görünüm:
        </span>
        <div
          role="group"
          aria-label="Görev süzgeci"
          className="flex flex-wrap gap-2 md:ml-auto md:justify-end"
        >
          {OGRENCI_SUZGECLERI.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={suzgec === s}
              onClick={() => setSuzgec(s)}
              className={cn(
                "min-h-dokunma rounded-rozet text-metin-koyu bg-notr-0 px-3.5 text-sm font-semibold",
                suzgec === s ? "ring-mor-500 ring-2" : "ring-mor-kenar hover:bg-mor-50 ring-1",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/*
        "RAPOR GİRMEDİN" ÖZETİ — geçmiş günde kırmızı kalan kart, kapalı bir
        gün bloğunun içinde görünmeyebilir. Sayı üstte: öğrenci hangi güne
        bakacağını bilsin.
      */}
      {eksikSayisi > 0 && (
        <p
          role="status"
          className="border-bekliyor-nokta bg-bekliyor-zemin text-bekliyor-metin flex items-center gap-2 rounded-xl border border-dashed px-3 py-2 text-sm"
        >
          <Ikon ad="uyari" className="size-4 shrink-0" />
          Bu hafta {eksikSayisi} ders için henüz rapor girmedin. Kırmızı kartlarda sonucunu
          girebilirsin; kayıt “sonradan girildi” olarak işaretlenir.
        </p>
      )}

      {gunler.map((gun, i) => {
        const gunun = gorevler
          .filter((g) => g.itemDate.slice(0, 10) === gun && suzgeceUyar(g, suzgec, bugun))
          .sort((a, b) => a.sortOrder - b.sortOrder);
        // Süzgeç seçiliyken eşleşmesi olmayan gün gizli: "Tamamlanan"a basan öğrenci
        // yedi boş şerit arasında arama yapmasın.
        if (suzgec !== "Tümü" && gunun.length === 0) return null;
        const gunAcik = acik.has(gun) || suzgec !== "Tümü";
        const govdeId = `program-gunu-${gun}`;
        const adet = gorevler.filter((g) => g.itemDate.slice(0, 10) === gun).length;
        return (
          <section
            key={gun}
            aria-label={HAFTA_GUNLERI[i]?.ad}
            className="bg-notr-0 border-mor-kenar overflow-hidden rounded-[1.25rem] border"
          >
            <h2>
              <button
                type="button"
                aria-expanded={gunAcik}
                aria-controls={govdeId}
                onClick={() => gunDegistir(gun)}
                className={cn(
                  "min-h-dokunma text-metin-koyu flex w-full items-center gap-2 px-4 py-3 text-left",
                  gunAcik && "bg-mor-50 border-mor-kenar border-b",
                )}
              >
                <Ikon ad="takvim" className="text-mor-500 size-4" />
                <span className="flex-1 font-medium">
                  {HAFTA_GUNLERI[i]?.ad}
                  {gun === bugun && (
                    <span className="text-notr-0 rounded-rozet ml-2 bg-(image:--gradyan-unk-birincil) px-2 py-0.5 text-[0.6875rem] font-bold">
                      Bugün
                    </span>
                  )}
                </span>
                <span className="text-metin-ikincil text-xs tabular-nums">
                  {adet === 0 ? "—" : `${adet} görev`}
                </span>
                <span className="text-metin-ikincil text-sm tabular-nums">{noktaliTarih(gun)}</span>
                <Ikon
                  ad="asagiOk"
                  className={cn("size-4 transition-transform", gunAcik && "rotate-180")}
                />
              </button>
            </h2>
            {gunAcik && (
              <div id={govdeId} className="p-3 md:p-4">
                {gunun.length === 0 ? (
                  <p className="text-metin-ikincil text-sm">
                    Koçun bu güne bir şey planlamamış. Boş gün de programın parçası.
                  </p>
                ) : (
                  <ul className="grid gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
                    {gunun.map((g) => (
                      <ProgramKarti
                        key={g.id}
                        gorev={g}
                        bugun={bugun}
                        giris={girilebilir.has(g.id) ? gecmisGunGirisi : undefined}
                        bekliyor={gecmisGunGirisi?.bekleyen.has(g.id) ?? false}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}

/*
  Kartın üstten beyaza eriyen zemini (tarama-2 §2.3 "durum kartları"): sınıf
  adları TAM yazılı, Tailwind birleştirilmiş adı taramıyor.
*/
const KART_ZEMINI: Record<UnkDurum, string> = {
  tamamlandi: "from-tamam-zemin",
  eksik: "from-eksik-zemin",
  bekliyor: "from-bekliyor-zemin",
  planli: "from-planli-zemin",
};

function ProgramKarti({
  gorev: g,
  bugun,
  giris,
  bekliyor,
}: {
  gorev: Gorev;
  bugun: string;
  /** Verilirse kart girişe açık: "Sonucu gir" / "Yapamadım". */
  giris?: GecmisGunGirisi;
  bekliyor: boolean;
}) {
  const [notAcik, setNotAcik] = useState(false);
  const { durum, etiket } = gorunenDurum(g, bugun);
  const dersAdi = g.lesson.name;
  const girildi = (g.correctCount ?? 0) + (g.wrongCount ?? 0) + (g.emptyCount ?? 0);
  return (
    <li
      aria-label={`${dersAdi}${g.topic?.name ? ` · ${g.topic.name}` : ""}`}
      className={cn(
        "to-notr-0 flex min-w-0 flex-col gap-2 rounded-2xl border-l-[5px] bg-linear-to-b p-4 break-words shadow-(--golge-unk-kart)",
        durum
          ? [DURUM_SINIFLARI[durum].cizgi, KART_ZEMINI[durum]]
          : "border-l-notr-300 from-notr-50",
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={cn(
            "grid size-9 shrink-0 place-items-center rounded-full text-[0.625rem] font-bold ring-1 ring-current/20",
            /*
              DERS DAİRESİ TEK PALETTEN (9a kararı, tokens.css "DERS RENKLERİ"): ViewAsStudent'ın
              kendi daire renkleri Reports göstergesiyle çelişiyordu; aynı ders her yerde aynı renk.
              Dolu nokta rengi + beyaz yazı açık tonlarda (limon, zümrüt) okunmuyordu (ölçüldü,
              kanit-9b): chip çifti kullanılıyor — açık zemin, aynı ailenin koyusu yazı (≥5,4).
            */
            dersRengi(dersAdi).chip,
          )}
        >
          {dersKisaltmasi(dersAdi)}
        </span>
        <span className="text-metin-koyu min-w-0 flex-1 self-center text-lg leading-tight font-medium uppercase">
          {dersAdi}
        </span>
        {durum ? (
          <DurumRozeti durum={durum} className="shrink-0">
            {etiket}
          </DurumRozeti>
        ) : (
          <Rozet>{etiket}</Rozet>
        )}
      </div>
      <p className="text-metin-ikincil flex items-center gap-1 text-sm tabular-nums">
        <Ikon ad="takvim" className="size-3.5" />
        {noktaliTarih(g.itemDate)}
        {g.startTime && <span>· {g.startTime}</span>}
      </p>
      <dl className="text-metin-koyu flex flex-col gap-1 text-sm">
        {g.topic?.name && <Satir ad="Konu" deger={g.topic.name} />}
        {g.kaynakAdi && <Satir ad="Kaynak" deger={g.kaynakAdi} />}
        {(g.pageFrom !== null || g.pageTo !== null) && (
          <Satir ad="Sayfa" deger={`${g.pageFrom ?? "?"}–${g.pageTo ?? "?"}`} />
        )}
        {(g.testFrom !== null || g.testTo !== null) && (
          <Satir ad="Test aralığı" deger={`${g.testFrom ?? "?"}–${g.testTo ?? "?"}`} />
        )}
        {g.targetQuestionCount !== null && <Satir ad="Soru" deger={g.targetQuestionCount} />}
        {g.targetTestCount !== null && <Satir ad="Test" deger={g.targetTestCount} />}
        {girildi > 0 && (
          <Satir
            ad="Sonucun"
            deger={`${g.correctCount ?? 0} doğru · ${g.wrongCount ?? 0} yanlış · ${g.emptyCount ?? 0} boş`}
          />
        )}
      </dl>
      {(g.topicPdfUrl || g.testPdfUrl || g.youtubeUrl) && (
        <div className="flex flex-wrap gap-2">
          <HapBaglanti url={g.topicPdfUrl} etiket="Konu PDF" />
          <HapBaglanti url={g.testPdfUrl} etiket="Test PDF" />
          <HapBaglanti
            url={g.youtubeUrl}
            etiket={g.videoTitle ? `Video · ${g.videoTitle}` : "Video"}
          />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded={notAcik}
          onClick={() => setNotAcik((v) => !v)}
          className="min-h-dokunma bg-notr-0 ring-mor-kenar text-metin-koyu hover:bg-mor-50 inline-flex items-center gap-1.5 rounded-xl px-3 text-sm ring-1"
        >
          <Ikon ad="defter" className="size-4" />
          Notlar
        </button>
        {g.isExcused && <Rozet>Mazeretli</Rozet>}
        {g.lateEnteredAt && <Rozet ton="uyari">Sonradan girildi</Rozet>}
        {bekliyor && <Rozet ton="bilgi">Gönderilmeyi bekliyor</Rozet>}
      </div>
      {notAcik && (
        <p className="border-notr-300 bg-notr-0 text-metin-koyu rounded-xl border border-dashed p-3 text-sm whitespace-pre-wrap">
          {g.coachNote ?? "Koçun bu göreve not eklememiş."}
        </p>
      )}
      {durum === "eksik" && (
        <p className="border-bekliyor-nokta bg-bekliyor-zemin text-bekliyor-metin flex items-center gap-2 rounded-xl border border-dashed px-3 py-2 text-sm">
          <Ikon ad="uyari" className="size-4 shrink-0" />
          Bu ders için henüz rapor girmedin.
        </p>
      )}
      {giris ? (
        <div className="mt-auto flex gap-2 pt-1">
          <Buton onClick={() => giris.onVeriGir(g)} className="flex-1">
            {girildi > 0 ? "Düzenle" : "Sonucu gir"}
          </Buton>
          <Buton gorunum="sessiz" onClick={() => giris.onYapamadim(g)} className="shrink-0">
            Yapamadım
          </Buton>
        </div>
      ) : (
        <p className="bg-saltokunur-zemin text-saltokunur-metin mt-auto flex items-center gap-2 rounded-xl px-3 py-2 text-sm">
          <Ikon ad="gorunum" className="size-4 shrink-0" />
          {kapaliNedeni(g, bugun)}
        </p>
      )}
    </li>
  );
}

/** Giriş kapalıyken şeritteki cümle — eski panelin "Salt-okunur mod"u, öğrenciye nedeniyle. */
function kapaliNedeni(g: Gorev, bugun: string): string {
  const gun = g.itemDate.slice(0, 10);
  if (g.isExcused) return "Mazeretli gün — sonuç beklenmiyor";
  if (g.status === "POSTPONED") return "Başka bir güne ertelendi";
  if (gun > bugun) return "Günü gelince sonucunu girebilirsin";
  if (g.renk === "YESIL") return "Salt-okunur · sonucun kaydedildi";
  return "Salt-okunur · hafta kapandı, düzeltmeyi koçun yapar";
}

function Satir({ ad, deger }: { ad: string; deger: string | number }) {
  return (
    <div>
      <dt className="inline font-bold">{ad}:</dt> <dd className="inline">{deger}</dd>
    </div>
  );
}

/**
 * Beyaz hap bağlantı. Şema kontrolü `ProgramBaglantisi` ile AYNI
 * (`baglantiSemasi`): `javascript:` gibi bir değer bağlantıya dönüşmüyor —
 * görünüm değişti diye güvenlik kuralı gevşemez.
 */
function HapBaglanti({ url, etiket }: { url: string | null; etiket: string }) {
  const sonuc = baglantiSemasi.safeParse(url);
  if (!sonuc.success) return null;
  return (
    <a
      href={sonuc.data}
      target="_blank"
      rel="noopener noreferrer"
      className="min-h-dokunma bg-notr-0 ring-mor-kenar text-metin-koyu hover:bg-mor-50 inline-flex max-w-full items-center gap-1.5 rounded-xl px-3 text-sm ring-1"
    >
      <Ikon ad="indir" className="size-4 shrink-0" />
      {etiket}
    </a>
  );
}

function KareDugme({
  etiket,
  onClick,
  children,
}: {
  etiket: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={etiket}
      className="bg-notr-0 ring-mor-kenar text-metin-koyu hover:bg-mor-50 grid size-11 place-items-center rounded-xl text-xl ring-1"
    >
      {children}
      <span className="sr-only">{etiket}</span>
    </button>
  );
}
